import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { ALL_LEAGUES } from "../constants";
import { useAdmin } from "../AdminContext";
import KalibrasyonView from "./KalibrasyonView";

// Renk yardımcıları
const C = {
  gpt: { color: "#10b981", bg: "#ecfdf5", border: "#6ee7b7", icon: "🟢" },
};

// "İlk analiz X · Y güncellendi" — yeniden analiz edilmişse ikinci parça çıkar.
// updated_at, created_at'ten anlamlı ölçüde (>1 dk) sonraysa güncelleme sayılır.
function analizZamani(pred) {
  const fmt = (s) => {
    if (!s) return null;
    const d = new Date(s.includes("Z") || s.includes("+") ? s : s + "Z");
    if (isNaN(d)) return null;
    return d.toLocaleString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };
  const oluş = fmt(pred.created_at);
  const günc = fmt(pred.updated_at);
  if (!oluş) return null;
  const farkVar = pred.updated_at && pred.created_at &&
    (new Date(pred.updated_at).getTime() - new Date(pred.created_at).getTime()) > 60000;
  return farkVar ? `İlk analiz ${oluş} · güncellendi ${günc}` : `Analiz ${oluş}`;
}

function predLabel(pred, home, away) {
  if (!pred) return "—";
  if (pred === "Beraberlik" || pred.toLowerCase() === "beraberlik") return "🤝 Beraberlik";
  const p = pred.toLowerCase();
  if (p.includes("deplasman") || p === "away_win" || pred.includes(away)) return `✈️ ${away}`;
  if (p.includes("ev sahibi") || p === "home_win" || pred.includes(home)) return `🏠 ${home}`;
  return pred;
}

function ResultBadge({ pred, actual, home, away }) {
  if (!pred || !actual) return <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>;
  const p = pred.toLowerCase();
  const predNorm = (pred === "Beraberlik" || p === "beraberlik") ? "draw"
    : (p.includes("deplasman") || p === "away_win" || pred.includes(away)) ? "away_win"
    : "home_win";
  const correct = predNorm === actual;
  return (
    <span style={{
      fontSize: 11, padding: "2px 7px", borderRadius: 4, fontWeight: 600,
      background: correct ? "#f0fdf4" : "#fef2f2",
      color: correct ? "#16a34a" : "#dc2626",
      border: `1px solid ${correct ? "#bbf7d0" : "#fecaca"}`,
    }}>
      {correct ? "✓" : "✗"}
    </span>
  );
}

function ReportModal({ pred, onClose }) {
  const reports = pred.gpt_reports;
  const [activeReport, setActiveReport] = useState("form");

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: "#fff", borderRadius: 12, width: "100%", maxWidth: 680,
        maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0",
          display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {pred.home} vs {pred.away}
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>{pred.league} · {pred.match_date}</div>
            {analizZamani(pred) && (
              <div style={{ fontSize: 11, color: "#b0aa9a", marginTop: 2 }}>🕐 {analizZamani(pred)}</div>
            )}
          </div>
          <button onClick={onClose} style={{ fontSize: 18, border: "none",
            background: "none", cursor: "pointer", color: "#9ca3af" }}>✕</button>
        </div>

        {/* Tahmin özeti — GPT */}
        {(() => {
          const aiPred      = pred.gpt_prediction || (pred.ai_provider === "gpt" ? pred.prediction : null);
          const aiHp        = pred.gpt_home_pct   ?? (pred.ai_provider === "gpt" ? pred.home_pct  : null);
          const aiDp        = pred.gpt_draw_pct   ?? (pred.ai_provider === "gpt" ? pred.draw_pct  : null);
          const aiAp        = pred.gpt_away_pct   ?? (pred.ai_provider === "gpt" ? pred.away_pct  : null);
          const aiConf      = pred.gpt_confidence ?? (pred.ai_provider === "gpt" ? pred.confidence : null);
          const aiReasoning = pred.gpt_reasoning  || (pred.ai_provider === "gpt" ? pred.reasoning  : null);
          const cfg = C.gpt;
          if (!aiPred) return null;
          const winner = (aiPred === "Beraberlik" || aiPred?.toLowerCase() === "beraberlik") ? "draw"
            : (aiPred?.toLowerCase().includes("deplasman") || aiPred?.includes(pred.away)) ? "away"
            : "home";
          return (
            <div style={{ padding: "12px 20px", background: cfg.bg,
              borderBottom: "1px solid #f0f0f0" }}>
              {/* Karar + güven */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                <span style={{ fontSize: 20 }}>
                  {winner === "home" ? "🏠" : winner === "away" ? "✈️" : "🤝"}
                </span>
                <span style={{ fontSize: 15, fontWeight: 700, color: cfg.color }}>{aiPred}</span>
                {aiConf != null && (
                  <span style={{ marginLeft: "auto", fontSize: 12, color: "#9ca3af",
                    background: "#fff", padding: "3px 10px", borderRadius: 20,
                    border: "1px solid #e5e7eb" }}>
                    Güven: {aiConf}/100
                  </span>
                )}
              </div>
              {/* Olasılık barı */}
              {aiHp != null && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", height: 8, borderRadius: 4,
                    overflow: "hidden", gap: 1, marginBottom: 4 }}>
                    <div style={{ flex: aiHp, background: "#2563eb" }} />
                    <div style={{ flex: aiDp, background: "#d97706" }} />
                    <div style={{ flex: aiAp, background: "#dc2626" }} />
                  </div>
                  <div style={{ display: "flex", gap: 16, fontSize: 11, color: "#6b7280" }}>
                    <span><span style={{ color: "#2563eb", fontWeight: 600 }}>🏠 {pred.home}</span> %{aiHp}</span>
                    <span><span style={{ color: "#d97706", fontWeight: 600 }}>🤝 Beraberlik</span> %{aiDp}</span>
                    <span><span style={{ color: "#dc2626", fontWeight: 600 }}>✈️ {pred.away}</span> %{aiAp}</span>
                  </div>
                  {/* Odds API satırı */}
                  {pred.odds_bookmakers > 0 ? (
                    <div style={{ marginTop: 6, padding: "4px 8px", background: "#f0fdf4",
                      borderRadius: 4, border: "1px solid #bbf7d0", fontSize: 11 }}>
                      <span style={{ color: "#15803d", fontWeight: 600 }}>📊 Piyasa</span>
                      <span style={{ color: "#166534", marginLeft: 6 }}>
                        🏠 %{pred.odds_ev} · 🤝 %{pred.odds_ber} · ✈️ %{pred.odds_dep}
                      </span>
                      <span style={{ color: "#6b7280", marginLeft: 6 }}>({pred.odds_bookmakers} bookie)</span>
                    </div>
                  ) : (
                    <div style={{ marginTop: 6, padding: "4px 8px", background: "#f9fafb",
                      borderRadius: 4, border: "1px solid #e5e7eb", fontSize: 11, color: "#9ca3af" }}>
                      📊 Piyasa verisi mevcut değil
                    </div>
                  )}
                </div>
              )}
              {/* Gerekçe */}
              {aiReasoning && (
                <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.6,
                  background: "#fff", borderRadius: 6, padding: "8px 12px",
                  border: "1px solid #e5e7eb", marginTop: 4 }}>
                  💬 {aiReasoning}
                </div>
              )}
            </div>
          );
        })()}

        {/* Rapor içeriği */}
        <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
          {reports ? (
            <>
              <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                {[["form","📊 Form"],["stats","📈 İstatistik"],["news","📰 Haberler"],
                  ["home_case",`🏠 ${pred.home}`],["away_case",`✈️ ${pred.away}`],
                  ["odds","📊 Piyasa"],["kalibrasyon","🎯 Kalibrasyon"]].map(([k,v]) => (
                  <button key={k} onClick={() => setActiveReport(k)} style={{
                    padding: "5px 12px", borderRadius: 16, fontSize: 12,
                    border: `1px solid ${activeReport === k ? "#374151" : "#e5e7eb"}`,
                    background: activeReport === k ? "#111827" : "#fff",
                    color: activeReport === k ? "#fff" : "#6b7280",
                    cursor: "pointer", fontWeight: activeReport === k ? 600 : 400,
                  }}>{v}</button>
                ))}
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.7, color: "#374151",
                background: "#f9fafb", borderRadius: 8, padding: 14,
                whiteSpace: "pre-wrap" }}>
                {activeReport === "kalibrasyon" ? (
                  <KalibrasyonView pred={pred} />
                ) : activeReport === "odds" ? (
                  pred.odds_bookmakers > 0 ? (
                  <div>
                    <div style={{ marginBottom: 12, fontWeight: 600, color: "#15803d", fontSize: 14 }}>
                      📊 Piyasa Oranları ({pred.odds_bookmakers} Bookmaker)
                    </div>
                    {[
                      { label: `🏠 ${pred.home} Kazanır`, pct: pred.odds_ev, color: "#2563eb" },
                      { label: "🤝 Beraberlik", pct: pred.odds_ber, color: "#d97706" },
                      { label: `✈️ ${pred.away} Kazanır`, pct: pred.odds_dep, color: "#dc2626" },
                    ].map(({ label, pct, color }) => (
                      <div key={label} style={{ marginBottom: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color }}>{label}</span>
                          <span style={{ fontSize: 16, fontWeight: 800, color }}>%{pct}</span>
                        </div>
                        <div style={{ height: 8, background: "#e5e7eb", borderRadius: 4, overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 4 }} />
                        </div>
                      </div>
                    ))}
                    <div style={{ marginTop: 12, fontSize: 12, color: "#6b7280", fontStyle: "italic" }}>
                      Tahmin sırasında Bayesian blending'e %50 ağırlıkla dahil edildi.
                    </div>
                  </div>
                  ) : (
                  <div style={{ textAlign: "center", padding: "32px 16px", color: "#9ca3af" }}>
                    <div style={{ fontSize: 28, marginBottom: 10 }}>📊</div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#6b7280", marginBottom: 6 }}>
                      Piyasa verisi mevcut değil
                    </div>
                    <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.6 }}>
                      Bu maç için The Odds API'den oran alınamadı.<br/>
                      Sezon sonu play-off grubu veya desteklenmeyen takımlar için piyasa verisi bulunmayabilir.
                    </div>
                  </div>
                  )
                ) : (reports[activeReport] || "Bu rapor mevcut değil.")}
              </div>
            </>
          ) : (
            <div style={{ color: "#9ca3af", textAlign: "center", padding: 40 }}>
              GPT-4o analizi henüz yapılmamış.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AccuracyCard({ predictions, pending = 0 }) {
  const stats = { gpt: { total: 0, correct: 0 } };
  const leagueStats = {};

  predictions.forEach(p => {
    if (!p.actual_result) return;

    // Lig bazlı istatistik
    const lg = p.league || "Diğer";
    if (!leagueStats[lg]) leagueStats[lg] = { total: 0, correct: 0 };
    leagueStats[lg].total++;

    ["gpt"].forEach(ai => {
      const pred = p[`${ai}_prediction`] || (p.ai_provider === ai ? p.prediction : null);
      if (!pred) return;
      stats[ai].total++;
      const norm = (pred === "Beraberlik" || pred.toLowerCase() === "beraberlik") ? "draw"
        : (pred.toLowerCase().includes("deplasman") || pred.includes(p.away)) ? "away_win"
        : "home_win";
      if (norm === p.actual_result) stats[ai].correct++;
    });

    // Lig bazlı: GPT doğruysa doğru say
    const gpred = p.gpt_prediction || (p.ai_provider === "gpt" ? p.prediction : null);
    const anyCorrect = gpred ? (() => {
      const norm = gpred.toLowerCase().includes("beraberlik") ? "draw"
        : gpred.toLowerCase().includes("deplasman") ? "away_win" : "home_win";
      return norm === p.actual_result;
    })() : false;
    if (anyCorrect) leagueStats[lg].correct++;
  });

  const total = predictions.length;
  if (total === 0) return null;

  const allCorrect = stats.gpt.correct;
  const allTotal   = stats.gpt.total;
  const overallAcc = allTotal > 0 ? Math.round(allCorrect / allTotal * 100) : null;

  const topLeagues = Object.entries(leagueStats)
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 4);

  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
      padding: "16px 20px", marginBottom: 16 }}>

      {/* Üst satır: genel özet */}
      <div style={{ display: "flex", gap: 32, alignItems: "flex-end", marginBottom: 16,
        paddingBottom: 16, borderBottom: "1px solid #f3f4f6", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase",
            letterSpacing: 1, marginBottom: 4 }}>Genel Başarı</div>
          <div style={{ fontSize: 32, fontWeight: 800,
            color: overallAcc >= 60 ? "#16a34a" : overallAcc >= 45 ? "#f59e0b" : "#dc2626" }}>
            {overallAcc !== null ? `%${overallAcc}` : "—"}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase",
            letterSpacing: 1, marginBottom: 4 }}>Toplam</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: "#111827" }}>{total}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase",
            letterSpacing: 1, marginBottom: 4 }}>Doğru</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: "#16a34a" }}>{allCorrect}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase",
            letterSpacing: 1, marginBottom: 4 }}>Yanlış</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: "#dc2626" }}>{allTotal - allCorrect}</div>
        </div>
        {pending > 0 && (
          <div>
            <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase",
              letterSpacing: 1, marginBottom: 4 }}>Bekleyen</div>
            <div style={{ fontSize: 32, fontWeight: 700, color: "#9ca3af" }}>{pending}</div>
          </div>
        )}

        {/* Lig dağılımı */}
        {topLeagues.length > 0 && (
          <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", gap: 6, minWidth: 200 }}>
            {topLeagues.map(([lg, s]) => {
              const acc = Math.round(s.correct / s.total * 100);
              return (
                <div key={lg} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <span style={{ color: "#374151", flex: 1 }}>{lg}</span>
                  <span style={{ color: "#9ca3af", fontSize: 12 }}>{s.correct}/{s.total}</span>
                  <span style={{ fontWeight: 700, minWidth: 36, textAlign: "right",
                    color: acc >= 60 ? "#16a34a" : acc >= 45 ? "#f59e0b" : "#dc2626" }}>
                    %{acc}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Alt satır: GPT istatistik */}
      <div style={{ display: "flex", gap: 10 }}>
        {["gpt"].map(ai => {
          const s = stats[ai];
          const acc = s.total > 0 ? Math.round(s.correct / s.total * 100) : null;
          const cfg = C[ai];
          return (
            <div key={ai} style={{ display: "flex", alignItems: "center", gap: 10,
              padding: "8px 14px", borderRadius: 8,
              background: cfg.bg, border: `1px solid ${cfg.border}` }}>
              <span style={{ fontSize: 14 }}>{cfg.icon}</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: cfg.color }}>GPT-4o</div>
                {s.total > 0 ? (
                  <div style={{ fontSize: 11, color: "#6b7280" }}>
                    {s.correct}/{s.total} doğru
                    {acc !== null && (
                      <span style={{ marginLeft: 5, fontWeight: 700,
                        color: acc >= 50 ? "#16a34a" : "#dc2626" }}>%{acc}</span>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: "#d1d5db" }}>sonuç yok</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function History({ highlightId, active }) {
  const { isAdmin, secret } = useAdmin();
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [league, setLeague] = useState("");

  const [reportPred, setReportPred] = useState(null);
  const highlightRef = useRef(null);

  const load = (lg) => {
    setLoading(true);
    // Public referans (Süper Lig + gösterim=açık ligler) + kullanıcının açtıkları.
    // Sadece getPredictions (kullanıcıya özel) kullanılırsa otomatik/public
    // tahminler görünmez ve sayfa boş gelir.
    Promise.all([
      api.getPublicPredictions(lg || "").then(d => d.predictions || []).catch(() => []),
      api.getPredictions(lg || "").then(d => d.predictions || []).catch(() => []),
    ]).then(([pub, mine]) => {
      const byKey = {};
      for (const r of pub)  byKey[`${r.home}|${r.away}|${r.match_date}`] = r;
      for (const r of mine) byKey[`${r.home}|${r.away}|${r.match_date}`] = r;
      setPredictions(Object.values(byKey));
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  // Hızlı filtre uygula


  // Sonuçlanmış maçlar
  const filteredPredictions = predictions.filter(p => !!p.actual_result);
  // Bekleyen maçlar (sonuç gelmemiş)
  const pendingPredictions = predictions.filter(p => !p.actual_result);

  useEffect(() => { load(league); }, [league]);
  useEffect(() => { if (active) load(league); }, [active]); // eslint-disable-line

  useEffect(() => {
    if (highlightId && highlightRef.current) {
      setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
    }
  }, [highlightId, predictions]);

  // Ctrl+Alt+R → sıfırla (yalnızca admin girişi yapılmışsa)
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.altKey && (e.key === "r" || e.key === "R")) {
        e.preventDefault();
        if (!isAdmin) {
          window.alert("Tahminleri silmek için Admin panelinden giriş yapmalısın.");
          return;
        }
        if (window.confirm("Tüm tahminler silinecek. Emin misin?")) {
          api.resetPredictions(secret).then(() => { setPredictions([]); });
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isAdmin, secret]);

  const handleDelete = async (id) => {
    if (!isAdmin) return;
    const r = await api.deletePrediction(id, secret);
    // Backend yetkiyi de dogrular; 401 gelirse listeden dusurme.
    if (r && r.detail === "Unauthorized") {
      window.alert("Yetki reddedildi — admin oturumun düşmüş olabilir.");
      return;
    }
    setPredictions(prev => prev.filter(p => p.id !== id));
  };

  const actualLabel = (actual, home, away) => {
    if (!actual) return null;
    if (actual === "draw") return "🤝 Beraberlik";
    if (actual === "home_win") return `🏠 ${home}`;
    return `✈️ ${away}`;
  };

  const scoreStr = (hg, ag) => {
    if (hg == null || ag == null) return "—";
    return `${hg} - ${ag}`;
  };

  return (
    <div style={{ paddingBottom: 24 }}>

      {/* Özet */}
      {!loading && predictions.length > 0 && (
        <AccuracyCard predictions={predictions} pending={predictions.filter(p => !p.actual_result).length} />
      )}

      {/* Filtre + başlık */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
        padding: "10px 16px", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
            Tahminler ({filteredPredictions.length})
          </span>
          <select value={league} onChange={e => setLeague(e.target.value)} style={{
            marginLeft: "auto", fontSize: 12, padding: "4px 10px",
            border: "1px solid #e5e7eb", borderRadius: 5, background: "#fff",
          }}>
            <option value="">Tüm Ligler</option>
            {ALL_LEAGUES.filter(l => l).map(l => <option key={l}>{l}</option>)}
          </select>
        </div>
      </div>

      {loading && <div className="loading">Yükleniyor...</div>}
      {!loading && predictions.length === 0 && (
        <div className="empty">Henüz kaydedilmiş tahmin yok.</div>
      )}
      {!loading && predictions.length > 0 && filteredPredictions.length === 0 && (
        <div className="empty" style={{ padding: 32, textAlign: "center", color: "#9ca3af" }}>
          Bu filtreye uyan tahmin yok.
        </div>
      )}

      {/* Tablo */}
      {!loading && filteredPredictions.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb",
          borderRadius: 8, overflow: "hidden" }}>
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                  {[
                    {label: "Maç", cls: ""},
                    {label: "Tarih", cls: "hide-mobile"},
                    {label: "Lig", cls: "hide-mobile"},
                    {label: "🟢 GPT-4o", cls: ""},
                    {label: "İY", cls: "hide-mobile"},
                    {label: "MS", cls: ""},
                    {label: "Sonuç", cls: ""},
                    {label: "", cls: ""},
                  ].map((h, i) => (
                    <th key={i} className={h.cls} style={{
                      padding: "10px 10px", textAlign: "left", fontSize: 11,
                      letterSpacing: 0.5, color: "#9ca3af",
                      textTransform: "uppercase", whiteSpace: "nowrap",
                      fontWeight: 600,
                    }}>{h.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPredictions.map((p, i) => {
                  const isHighlight = p.id === highlightId;
                  const actual = p.actual_result;
                  const actualStr = actualLabel(actual, p.home, p.away);

                  return (
                    <tr key={p.id}
                      ref={isHighlight ? highlightRef : null}
                      style={{
                        borderBottom: "1px solid #f3f4f6",
                        background: isHighlight ? "#eff6ff" : i % 2 === 0 ? "#fff" : "#fafafa",
                        transition: "background 0.15s",
                      }}
                    >
                      {/* Maç */}
                      <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                          {p.home} <span style={{ color: "#d1d5db", fontWeight: 400 }}>vs</span> {p.away}
                        </div>
                      </td>

                      {/* Tarih */}
                      <td className="hide-mobile" style={{ padding: "10px 10px", whiteSpace: "nowrap",
                        fontSize: 12, color: "#6b7280" }}>
                        {p.match_date || "—"}
                      </td>

                      {/* Lig */}
                      <td className="hide-mobile" style={{ padding: "10px 10px", fontSize: 12, color: "#6b7280",
                        whiteSpace: "nowrap" }}>
                        {p.league}
                      </td>

                      {/* GPT tahmini */}
                      {(() => {
                        const gptPred = p.gpt_prediction || (p.ai_provider === "gpt" ? p.prediction : null);
                        const gptHp = p.gpt_home_pct || (p.ai_provider === "gpt" ? p.home_pct : null);
                        const gptDp = p.gpt_draw_pct || (p.ai_provider === "gpt" ? p.draw_pct : null);
                        const gptAp = p.gpt_away_pct || (p.ai_provider === "gpt" ? p.away_pct : null);
                        const gptConf = p.gpt_confidence || (p.ai_provider === "gpt" ? p.confidence : null);
                        return (
                          <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                            {gptPred ? (
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: C.gpt.color }}>
                                  {predLabel(gptPred, p.home, p.away)}
                                </div>
                                <div style={{ fontSize: 11, color: "#9ca3af" }}>
                                  {gptHp}% / {gptDp}% / {gptAp}%
                                </div>
                                <div style={{ fontSize: 11, color: "#9ca3af" }}>
                                  Güven: {gptConf}
                                </div>
                              </div>
                            ) : (
                              <span style={{ fontSize: 11, color: "#d1d5db" }}>—</span>
                            )}
                          </td>
                        );
                      })()}

                      {/* İY Skoru */}
                      <td className="hide-mobile" style={{ padding: "10px 10px", textAlign: "center",
                        fontFamily: "monospace", fontSize: 13, fontWeight: 600,
                        color: "#374151", whiteSpace: "nowrap" }}>
                        {scoreStr(p.ht_home_goals, p.ht_away_goals)}
                      </td>

                      {/* MS Skoru */}
                      <td style={{ padding: "10px 12px", textAlign: "center",
                        fontFamily: "monospace", fontSize: 14, fontWeight: 700,
                        color: p.ft_home_goals != null ? "#111827" : "#d1d5db",
                        whiteSpace: "nowrap" }}>
                        {scoreStr(p.ft_home_goals, p.ft_away_goals)}
                      </td>

                      {/* Gerçek sonuç */}
                      <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                        {actualStr ? (
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600,
                              color: "#374151", marginBottom: 4 }}>{actualStr}</div>
                            <div style={{ display: "flex", gap: 4 }}>
                              <ResultBadge pred={p.gpt_prediction || (p.ai_provider === "gpt" ? p.prediction : null)}
                                actual={actual} home={p.home} away={p.away} />
                            </div>
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: "#d1d5db" }}>Bekleniyor</span>
                        )}
                      </td>

                      {/* Aksiyon */}
                      <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => setReportPred(p)} style={{
                            fontSize: 11, padding: "4px 10px", borderRadius: 5,
                            border: "1px solid #e5e7eb", background: "#fff",
                            color: "#6b7280", cursor: "pointer", whiteSpace: "nowrap",
                          }}>👁 Rapor</button>
                          {isAdmin && (
                            <button onClick={() => handleDelete(p.id)} style={{
                              fontSize: 11, padding: "4px 8px", borderRadius: 5,
                              border: "1px solid #fecaca", background: "#fff",
                              color: "#dc2626", cursor: "pointer",
                            }}>🗑</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bekleyen maçlar */}
      {!loading && pendingPredictions.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
          overflow: "hidden", marginBottom: 16 }}>
          <div style={{ padding: "10px 16px", background: "#f9fafb",
            borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
              Sonuç Beklenen ({pendingPredictions.length})
            </span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                {[
                  { label: "Maç" }, { label: "Tarih" }, { label: "Lig" },
                  { label: "🟢 GPT-4o Tahmini" }, { label: "" },
                ].map((h, i) => (
                  <th key={i} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11,
                    letterSpacing: 0.5, color: "#9ca3af", textTransform: "uppercase",
                    fontWeight: 600 }}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pendingPredictions.map((p, i) => {
                const pred = p.gpt_prediction || (p.ai_provider === "gpt" ? p.prediction : null);
                const evPct = p.gpt_home_pct || p.home_win_pct;
                const berPct = p.gpt_draw_pct || p.draw_pct;
                const depPct = p.gpt_away_pct || p.away_win_pct;
                return (
                  <tr key={p.id} style={{ borderBottom: "1px solid #f3f4f6",
                    background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                        {p.home} <span style={{ color: "#d1d5db", fontWeight: 400 }}>vs</span> {p.away}
                      </div>
                    </td>
                    <td style={{ padding: "10px 12px", fontSize: 12, color: "#6b7280" }}>
                      {p.match_date || "—"}
                    </td>
                    <td style={{ padding: "10px 12px", fontSize: 12, color: "#6b7280" }}>
                      {p.league || "—"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {pred ? (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1d4ed8" }}>{pred}</div>
                          {evPct && (
                            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                              {evPct}% / {berPct}% / {depPct}%
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: "#d1d5db" }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 11, background: "#f3f4f6", color: "#6b7280",
                        padding: "3px 8px", borderRadius: 12 }}>Bekleniyor</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Rapor modalı */}
      {reportPred && (
        <ReportModal pred={reportPred} onClose={() => setReportPred(null)} />
      )}
    </div>
  );
}
